import * as arrays from "common/arrays";
import * as numbers from "common/numbers";

const LAYER_STATUS_STAGE = {
  SHELVED: "IGNORE",
  CANCELLED: "IGNORE",
  DRAFT: "DRAFT",
  QUOTE_REQUESTED: "DRAFT",
  DECLINED_TO_QUOTE: "DRAFT",
  QUOTED: "QUOTED",
  BOUND: "BOUND",
  NOT_TAKEN_UP: "QUOTED",
};

// Sentinel paper provider id meaning "actively none": survives layer-default
// resolution (unlike null, which inherits the default) and is normalised back
// to null in resolveLayer so documents and generators see "no paper provider".
export const NONE_PAPER_PROVIDER = "NONE";

export const extractInPlayLayerIds = (layers) => {
  if (!arrays.hasItems(layers)) {
    return [];
  }

  const layerInfo = layers
    .map((layer) => ({
      id: layer.id,
      layer,
      stage: LAYER_STATUS_STAGE?.[layer?.status] ?? null,
    }))
    .filter(({ id, stage }) => id != null && stage !== "IGNORE");

  if (layerInfo.some(({ stage }) => stage === "BOUND")) {
    return layerInfo
      .filter(({ stage }) => stage == null || stage === "BOUND")
      .map(({ id }) => id);
  } else if (layerInfo.some(({ stage }) => stage === "QUOTED")) {
    return layerInfo
      .filter(({ stage }) => stage == null || stage === "QUOTED")
      .map(({ id }) => id);
  } else {
    return layerInfo.map(({ id }) => id);
  }
};

const DEFAULT_PROGRAM_STATUS = "DRAFT";

// Rollup of layer statuses to a program status. The precedence is hard-coded
// rather than read from config so that the same answer comes out of the site
// and of the selectors lambda, which has no access to static data.
//
// Highest wins when layers disagree. Keys are just strings, so a tenant that
// does not use one is unaffected. Anything not listed (SHELVED, CANCELLED) is
// ignored while layers disagree and only surfaces when it is the sole status.
export const PROGRAM_STATUS_PRECEDENCE = [
  "BOUND",
  "QUOTED",
  "NOT_TAKEN_UP",
  "NO_BROKER_RESPONSE",
  "INDICATED_NTU",
  "NTU_INDICATION",
  "DECLINED_TO_QUOTE",
  "QUOTE_REQUESTED",
  "DRAFT",
  "NOT_INPUT",
  "ON_HOLD",
];

export const extractProgramStatus = (
  layers,
  precedence = PROGRAM_STATUS_PRECEDENCE
) => {
  const statuses = [
    ...new Set((layers ?? []).map((l) => l?.status).filter((s) => s != null)),
  ];

  if (statuses.length === 0) return DEFAULT_PROGRAM_STATUS;
  if (statuses.length === 1) return statuses[0];
  if (statuses.includes("BOUND")) return "BOUND";

  return (
    precedence.find((status) => statuses.includes(status)) ??
    DEFAULT_PROGRAM_STATUS
  );
};

export const shortLayerName = (layer) =>
  `$${numbers.abbreviated(layer?.limit)} xs $${numbers.abbreviated(
    layer?.attachment
  )}`;

export const shortShareName = (layer) =>
  layer?.lineSize == null || layer?.lineSize === layer?.limit
    ? shortLayerName(layer)
    : `$${numbers.abbreviated(layer?.lineSize, 3)} po ${shortLayerName(layer)}`;
